import * as countryCodes from 'country-codes-list';
import { isSupportedCountry, parsePhoneNumberWithError } from 'libphonenumber-js/max';

type CountryData = {
  countryNameEn: string;
  countryNameLocal: string;
  countryCode: string;
  currencyCode: string;
  currencyNameEn: string;
  tinType: string;
  tinName: string;
  officialLanguageCode: string;
  officialLanguageNameEn: string;
  officialLanguageNameLocal: string;
  countryCallingCode: string;
  areaCodes?: any[];
  region: string;
  flag: string;
};

const countryDialCodeMap = new Map<string, string>();

export const getDialCodeByCountry = (countryCode: string): string | null => {
  if (countryDialCodeMap.has(countryCode)) {
    const cached = countryDialCodeMap.get(countryCode)!;
    return cached || null;
  }
  const all = countryCodes.all() as CountryData[];
  const country = all.find((c: CountryData) => c.countryCode.toUpperCase() === countryCode.toUpperCase());
  const dialCode = country?.countryCallingCode ? `+${country.countryCallingCode}` : null;
  countryDialCodeMap.set(countryCode, dialCode ?? '');
  return dialCode;
};

type FormatE164PhoneNumberParams = {
  phoneNumber: string;
  countryCode: string;
};

/**
 * Remove spaces and dashes from phone number
 * e.g., "+82 10-2544-1586" → "+821025441586"
 */
const sanitizePhoneNumber = (phone: string): string => {
  return phone.replace(/[\s\-]/g, '');
};

/**
 * Parses a phone number using its ISO country and returns canonical E.164.
 * Invalid numbers and missing or unsupported countries return an empty string.
 */
export interface ParsedPhoneNumber {
  countryCode: string;
  dialCode: string;
  localNumber: string;
}

/**
 * Parses an E.164 phone number into country code, dial code, and local number.
 * Requires the country ISO code (e.g. "MN", "SG") to resolve the dial code.
 *
 * @example
 * parseE164PhoneNumber({ phoneNumber: '+9761025441586', countryCode: 'MN' })
 * // → { countryCode: 'MN', dialCode: '+976', localNumber: '1025441586' }
 */
export const parseE164PhoneNumber = ({ phoneNumber, countryCode }: FormatE164PhoneNumberParams): ParsedPhoneNumber => {
  const sanitized = phoneNumber ? sanitizePhoneNumber(phoneNumber.trim()) : '';
  const dialCode = getDialCodeByCountry(countryCode) ?? '';

  if (dialCode && sanitized.startsWith(dialCode)) {
    return { countryCode, dialCode, localNumber: sanitized.slice(dialCode.length) };
  }

  if (sanitized.startsWith('+')) {
    return { countryCode, dialCode, localNumber: sanitized.slice(1) };
  }

  return { countryCode, dialCode, localNumber: sanitized };
};

export const formatE164PhoneNumber = ({ phoneNumber, countryCode }: FormatE164PhoneNumberParams): string => {
  const normalizedCountryCode = countryCode.trim().toUpperCase();
  const trimmedPhoneNumber = phoneNumber.trim();
  if (!trimmedPhoneNumber || !isSupportedCountry(normalizedCountryCode)) {
    return '';
  }

  let parsedPhoneNumber: ReturnType<typeof parsePhoneNumberWithError>;
  try {
    parsedPhoneNumber = parsePhoneNumberWithError(trimmedPhoneNumber, {
      defaultCountry: normalizedCountryCode,
      extract: false,
    });
  } catch {
    return '';
  }

  if (parsedPhoneNumber.ext !== undefined || !parsedPhoneNumber.isValid()) {
    return '';
  }

  return /^\+[1-9]\d{1,14}$/.test(parsedPhoneNumber.number) ? parsedPhoneNumber.number : '';
};
